import type { ComponentProps } from 'react';
import { Unavailable as Shared } from '@beyond-js/ui/react';
import { ApiProblem } from '@cdn/api';
import { useI18n } from '@cdn/i18n';
import styles from './styles.module.css';

type Properties = Omit<ComponentProps<typeof Shared>, 'labels'>;

/**
 * "Not available here" (decision D06): a missing role, admission, association or plan capability explained
 * in place, with who can change it, instead of a refusal card, a raw error or a hidden control. It never looks
 * like a failure of CDN. The family's component, with the family's words in the selected language. A way on drawn
 * as a link ends it by its words' baseline, one inset from the border, as a quiet button's words do (D184).
 */
export function Unavailable({ action, ...properties }: Properties) {
	const { locale } = useI18n();
	return <Shared level={2} {...properties} action={action ? <span className={styles.way}>{action}</span> : action} labels={Shared.labels[locale.code === 'es' ? 'es' : 'en']} />;
}

/** The refusals that are a standing rather than a failure, and what each one is about */
const REFUSALS: Record<string, { key: string; kind: 'access' | 'capability' }> = {
	FORBIDDEN: { key: 'role', kind: 'access' },
	ADMISSION_REQUIRED: { key: 'admission', kind: 'access' },
	ENTITLEMENT_REQUIRED: { key: 'plan', kind: 'capability' }
};

/**
 * A refusal the service answered because of who the person is here — their role, their admission, the
 * organization's plan — stated as unavailable, with what happened, that nothing changed and who can change
 * it. The code stays beside it as an identifier.
 *
 * @returns Nothing for any other problem, which the caller explains as a failure
 */
export function Refusal({ problem, action }: { problem: unknown; action?: Properties['action'] }) {
	const { t, translator } = useI18n();
	const read = ApiProblem.of(problem);
	const refusal = REFUSALS[read.code];
	if (!refusal || read.unknown) return null;
	return (
		<Unavailable
			kind={refusal.kind}
			title={t(`ui.refusal.${refusal.key}.title`)}
			reason={`${translator.error(read.code)} ${t('ui.refusal.unchanged')}`}
			owner={t(`ui.refusal.${refusal.key}.owner`)}
			code={read.code}
			action={action}
		/>
	);
}

/** Whether a problem is one of those refusals */
Refusal.covers = (problem: unknown) => {
	const read = ApiProblem.of(problem);
	return Boolean(REFUSALS[read.code]) && !read.unknown;
};
